import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog';
import useSwalWrapper from "@jumbo/vendors/sweetalert2/hooks";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Checkbox, FormControl, FormControlLabel, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import subTaskService from 'app/services/subtask_service';
import { useFormik } from "formik";
import * as yup from "yup";

const Div = Box;

// --- Utils ---
function getCurrentDate() {
    const d = new Date();
    return d.toISOString().split("T")[0];
}

const sweetAlerts = (Swal, variant) => {
    Swal.fire({
        icon: variant,
        title: variant === "success" ? "Success" : "Error",
        text: variant === "success" ? "Successfully updated payment status!" : "Something went wrong. Please try again."
    }).then(() => {
        if (variant === "success") {
            window.location.reload();
        }
    });
};

// --- Validation Schema (all fields required) ---
const validationSchema = yup.object({
    paymentStatus: yup
        .boolean()
        .oneOf([true], "You must mark as Paid"),
    paidBy: yup.string().required("Paid From is required"),
    paymentDate: yup.string().required("Payment Date is required"),
});

const AllOpPaymentStatusForm = ({ expenseIds, status, onSave }) => {
    const Swal = useSwalWrapper();
    const { hideDialog } = useJumboDialog();
    const currentDate = getCurrentDate();

    const formik = useFormik({
        initialValues: {
            paymentStatus: false,
            paymentDate: "",
            paidBy: "",
        },
        validationSchema,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: async (values, { setSubmitting }) => {
            const payload = {
                expenseIds,
                payment_status: values.paymentStatus,
                payment_date: values.paymentDate,
                paid_by: values.paidBy,
                status
            };

            try {
                await subTaskService.updateOpExpensePaymentStatus(payload);
                hideDialog();
                sweetAlerts(Swal, "success");
                onSave();
            } catch (err) {
                console.error("Payment Update Error:", err);
                setSubmitting(false);
                hideDialog();
                sweetAlerts(Swal, "error");
            }
        }
    });

    return (
        <Div sx={{ "& .MuiTextField-root": { mb: 3 } }}>
            <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
                <FormControl error={formik.touched.paymentStatus && Boolean(formik.errors.paymentStatus)}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formik.values.paymentStatus}
                                onChange={formik.handleChange}
                                name="paymentStatus"
                                color="primary"
                            />
                        }
                        label="Paid"
                    />
                    {formik.touched.paymentStatus && formik.errors.paymentStatus && (
                        <FormHelperText>{formik.errors.paymentStatus}</FormHelperText>
                    )}
                </FormControl>

                <FormControl
                    fullWidth
                    sx={{ mt: 2 }}
                    error={formik.touched.paidBy && Boolean(formik.errors.paidBy)}
                >
                    <InputLabel id="paid-by-label">Paid From</InputLabel>
                    <Select
                        labelId="paid-by-label"
                        id="paidBy"
                        name="paidBy"
                        value={formik.values.paidBy}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                    >
                        <MenuItem value="">
                            <em>Select Account</em>
                        </MenuItem>
                        <MenuItem value="Blackmilk Media Pvt Ltd -current a/c">Blackmilk Media Pvt Ltd -current a/c</MenuItem>
                        <MenuItem value="Pianosa Pvt Ltd - current a/c">Pianosa Pvt Ltd - current a/c</MenuItem>
                        <MenuItem value="Credit Card - 001">Credit Card - 001</MenuItem>
                        <MenuItem value="Credit Card - 007">Credit Card - 007</MenuItem>
                        <MenuItem value="Credit Card - 6003">Credit Card - 6003</MenuItem>
                        <MenuItem value="Credit Card - 6005">Credit Card - 6005</MenuItem>
                    </Select>
                    {formik.touched.paidBy && formik.errors.paidBy && (
                        <FormHelperText>{formik.errors.paidBy}</FormHelperText>
                    )}
                </FormControl>

                <TextField
                    id="paymentDate"
                    name="paymentDate"
                    label="Payment Date"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: currentDate }}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.paymentDate}
                    error={formik.touched.paymentDate && Boolean(formik.errors.paymentDate)}
                    helperText={formik.touched.paymentDate && formik.errors.paymentDate}
                    sx={{ mt: 3 }}
                />

                <LoadingButton
                    fullWidth
                    type="submit"
                    variant="contained"
                    size="large"
                    sx={{ mt: 2 }}
                    loading={formik.isSubmitting}
                    disabled={formik.isSubmitting}
                >
                    Save
                </LoadingButton>
            </form>
        </Div>
    );
};

export default AllOpPaymentStatusForm;
